/**
 * Unavailable Pickup Cart Attributes
 *
 * Sets cart attributes so Click & Collect locations that cannot fulfill the cart
 * are hidden in checkout. Run functions (local-pickup, pickup-delivery-customization)
 * read amb.unavailable_pickup_location_ids when delivery options are requested.
 *
 * Rules (must match checkout extension):
 * - Only cart lines that require shipping are considered.
 * - For each pickup location: can fulfill only if every shippable line has
 *   (store stock at location + warehouse stock) >= line quantity, and
 *   warehouse stock >= 10 (treat as out of stock below that).
 */

import { CartService } from './cart-service.js';
import { StorefrontService } from './storefront-service.js';
import { ThemeEvents } from '@theme/events';

const ATTR_UNAVAILABLE_IDS = 'amb.unavailable_pickup_location_ids';
const ATTR_UNAVAILABLE_HANDLES = 'amb.unavailable_pickup_handles';
const MIN_WAREHOUSE_STOCK = 10;

/**
 * Get config from window.ambPickupCartConfig (set by theme for AU/NZ) or from delivery-options data script.
 * @returns {{ storefront: { shopDomain: string, apiVersion: string, accessToken: string }, location_id: string | null } | null}
 */
function getConfig() {
  if (typeof window !== 'undefined' && window.ambPickupCartConfig) {
    return window.ambPickupCartConfig;
  }
  const script = document.querySelector('delivery-options script[name="data"]');
  if (script?.textContent) {
    try {
      const data = JSON.parse(script.textContent);
      if (data.storefront && data.location_id != null) {
        return { storefront: data.storefront, location_id: data.location_id };
      }
    } catch (_) {
      // ignore
    }
  }
  return null;
}

/**
 * Extract numeric ID from location id (GID or number).
 * @param {string} id - Location ID (e.g. "gid://shopify/Location/123" or "123")
 * @returns {string} Numeric ID
 */
function toNumericLocationId(id) {
  if (!id) return '';
  const str = String(id);
  if (!str.startsWith('gid://')) return str;
  const match = str.match(/gid:\/\/shopify\/\w+\/(\d+)/);
  return match ? match[1] : str;
}

/**
 * Normalize location ID for comparison (GID format).
 * @param {string} id
 * @returns {string}
 */
function toLocationGid(id) {
  if (!id) return '';
  if (String(id).startsWith('gid://')) return String(id);
  return `gid://shopify/Location/${id}`;
}

/**
 * Compute which pickup location IDs cannot fulfill the cart.
 * Fulfill = for every shippable line: (store at location + warehouse if >= 10) >= line quantity.
 *
 * @param {Array<Array<{ location: { id: string }, quantityAvailable: number, dcQuantityAvailable?: number }>>} allAvailabilities - Per-variant availability arrays
 * @param {number[]} quantities - Required quantity per variant (same order as allAvailabilities)
 * @param {Array<{ id: string }>} pickupLocations - Pickup locations to check (exclude warehouse-only if desired)
 * @param {string | null} warehouseLocationId - Warehouse location ID (numeric or GID); warehouse stock must be >= 10 to count
 * @returns {string[]} Numeric location IDs that cannot fulfill
 */
function computeUnavailableLocationIds(allAvailabilities, quantities, pickupLocations, warehouseLocationId) {
  const warehouseNumeric = warehouseLocationId ? toNumericLocationId(warehouseLocationId) : '';
  const unavailable = [];

  for (const location of pickupLocations) {
    const locationGid = toLocationGid(location.id);
    const locationIsWarehouse =
      warehouseNumeric && location.id ? toNumericLocationId(location.id) === warehouseNumeric : false;
    let canFulfill = true;

    for (let lineIndex = 0; lineIndex < allAvailabilities.length; lineIndex++) {
      const variantAvailabilities = allAvailabilities[lineIndex];
      const quantityRequired = quantities[lineIndex] ?? 1;

      const atLocation = variantAvailabilities.find((av) => av.location?.id === locationGid);
      const storeQty = atLocation?.quantityAvailable ?? 0;
      const warehouseQty = variantAvailabilities[0]?.dcQuantityAvailable ?? 0;
      const warehouseContribution =
        locationIsWarehouse ? 0 : warehouseQty >= MIN_WAREHOUSE_STOCK ? warehouseQty : 0;
      const effectiveQty = storeQty + warehouseContribution;
      const warehouseOk = locationIsWarehouse ? storeQty >= MIN_WAREHOUSE_STOCK : true;

      if (effectiveQty < quantityRequired || !warehouseOk) {
        canFulfill = false;
        break;
      }
    }

    if (!canFulfill) {
      unavailable.push(toNumericLocationId(location.id));
    }
  }

  return unavailable;
}

/**
 * Run: fetch cart, availability, compute unavailable IDs, update cart attributes.
 * No-op if config missing, cart empty, or no shippable lines.
 */
async function updateUnavailablePickupAttributes() {
  const config = getConfig();
  if (!config?.storefront?.accessToken) {
    return;
  }

  let cart;
  try {
    cart = await CartService.getCart();
  } catch (e) {
    return;
  }

  const items = cart.items || [];
  const shippable = items.filter((item) => item.requires_shipping);
  if (shippable.length === 0) {
    await CartService.updateAttributes({
      [ATTR_UNAVAILABLE_IDS]: '',
      [ATTR_UNAVAILABLE_HANDLES]: ''
    });
    return;
  }

  const variantIds = shippable.map((i) => String(i.variant_id));
  const quantities = shippable.map((i) => i.quantity);

  const storefrontService = new StorefrontService({
    shopDomain: config.storefront.shopDomain,
    apiVersion: config.storefront.apiVersion || '2026-01',
    accessToken: config.storefront.accessToken,
    locationId: config.location_id || undefined
  });

  let allLocations = [];
  let allAvailabilities = [];

  try {
    allLocations = await storefrontService.getAllLocations();
    allAvailabilities = await storefrontService.getStoreAvailabilities(variantIds, quantities);
  } catch (e) {
    return;
  }

  const pickupLocations = (allLocations || []).filter(
    (loc) => loc?.localPickup?.enabled !== false
  );
  if (pickupLocations.length === 0) {
    await CartService.updateAttributes({
      [ATTR_UNAVAILABLE_IDS]: '',
      [ATTR_UNAVAILABLE_HANDLES]: ''
    });
    return;
  }

  const warehouseLocationId = config.location_id || null;
  const unavailableIds = computeUnavailableLocationIds(
    allAvailabilities,
    quantities,
    pickupLocations,
    warehouseLocationId
  );

  const valueIds = unavailableIds.length > 0 ? unavailableIds.join(',') : '';
  const attributes = {
    [ATTR_UNAVAILABLE_IDS]: valueIds,
    [ATTR_UNAVAILABLE_HANDLES]: '' // Handles set in checkout UI extension if needed
  };

  await CartService.updateAttributes(attributes);
}

let debounceTimer = null;

function scheduleUpdate() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    updateUnavailablePickupAttributes();
  }, 300);
}

function init() {
  if (!getConfig()) {
    return;
  }
  document.addEventListener(ThemeEvents.cartUpdate, scheduleUpdate);
  scheduleUpdate();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
